import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DocEditorCore from "@/components/studio/DocEditorCore";
import SlideEditorCore from "@/components/studio/SlideEditorCore";
import DrawEditorCore from "@/components/studio/DrawEditorCore";
import SiteEditorCore from "@/components/studio/SiteEditorCore";
import SheetEditorCore from "@/components/studio/SheetEditorCore";
import EmailClientCore from "@/components/studio/EmailClientCore";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Lock, Award, Clock, ListChecks, Flag, ArrowLeft, Volume2 } from "lucide-react";
import { TaskStrip, Timer, BottomBar, ReadAloudBar } from "@/pages/unittask/Parts";
import { UnitTaskResult } from "@/pages/unittask/UnitTaskResult";
import { stopSpeaking } from "@/lib/speech";
import { useSecureTest, SecureOverlay, SecureRules, NoCopy, enterFullscreen, exitFullscreen } from "@/components/SecureTest";

const SAVE_MS = 20000;

export default function UnitTaskRunner() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const { taskId: rawTaskId } = useParams();
  const taskId = !rawTaskId || rawTaskId === "docs" ? "docs-unittask" : rawTaskId;
  const isGuide = user?.role === "guide";
  const [meta, setMeta] = useState(null);
  const [secureCfg, setSecureCfg] = useState(null);
  const [config, setConfig] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [states, setStates] = useState({});
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("intro");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const pageRef = useRef(null);
  const dirty = useRef(false);
  const statesRef = useRef(states); statesRef.current = states;
  const indexRef = useRef(index); indexRef.current = index;

  useEffect(() => {
    api.get(`/unit-task/${taskId}`).then((r) => { setMeta(r.data); return api.get(`/studio/${r.data.track}`); }).then((r) => setConfig(r.data.config)).catch(() => { setMeta(null); setConfig({}); });
    api.get("/me/secure-test").then((r) => setSecureCfg(r.data)).catch(() => setSecureCfg({ enabled: false }));
  }, [taskId]);
  const track = meta?.track || "docs";
  const kindLabel = meta?.kind === "midtask" ? "Mid-Unit Task Test" : "Unit Task";
  const secureOn = !isGuide && !!secureCfg?.enabled;

  const start = async () => {
    enterFullscreen();
    try {
      const r = await api.post(`/unit-task/${taskId}/start`);
      setAttempt(r.data);
      setStates(r.data.state || {});
      setIndex(r.data.screen_index || 0);
      setPhase("run");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && d.message) || (typeof d === "string" ? d : `Could not start the ${kindLabel}.`));
      if (e?.response?.status === 409) api.get(`/unit-task/${taskId}`).then((r) => setMeta(r.data));
    }
  };

  const save = useCallback(async (keepalive = false) => {
    if (!attempt || phase !== "run") return;
    const body = JSON.stringify({ state: statesRef.current, screen_index: indexRef.current });
    dirty.current = false;
    if (keepalive) {
      fetch(`${API}/unit-task/attempts/${attempt.attempt_id}/save`, { method: "PUT", credentials: "include", keepalive: true, headers: { "Content-Type": "application/json" }, body });
      return;
    }
    try { await api.put(`/unit-task/attempts/${attempt.attempt_id}/save`, JSON.parse(body)); } catch (e) { /* retry next tick */ }
  }, [attempt, phase]);

  useEffect(() => {
    if (phase !== "run") return;
    const iv = setInterval(() => { if (dirty.current) save(); }, SAVE_MS);
    const hide = () => { if (document.visibilityState === "hidden") save(true); };
    document.addEventListener("visibilitychange", hide);
    window.addEventListener("pagehide", hide);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", hide); window.removeEventListener("pagehide", hide); save(true); };
  }, [phase, save]);

  const secRef = useRef(null);
  const submit = useCallback(async (auto = false) => {
    if (!attempt || submitting) return;
    if (!auto) {
      const flagged = Object.values(states).reduce((n, s) => n + (s.flagged || []).length, 0);
      const msg = flagged ? `You still have ${flagged} flagged task${flagged > 1 ? "s" : ""}. Submit anyway?` : `Submit your ${kindLabel} for grading? You can't change it afterwards.`;
      const ok = secRef.current ? secRef.current.suspend(() => window.confirm(msg)) : window.confirm(msg);
      if (!ok) return;
    }
    setSubmitting(true);
    secRef.current?.stop();
    try {
      const r = await api.post(`/unit-task/attempts/${attempt.attempt_id}/submit`, { state: statesRef.current, reason: auto === "integrity" ? "integrity" : null });
      setResult(r.data);
      setPhase("done");
      exitFullscreen();
      if (!isGuide) refresh();
      if (auto === "integrity") toast.error(`You left the test again — your ${kindLabel} was submitted automatically.`);
      else if (auto) toast.warning(`Time's up — your ${kindLabel} was submitted automatically.`);
    } catch (e) { toast.error("Could not submit. Please try again."); }
    finally { setSubmitting(false); }
  }, [attempt, submitting, states, isGuide, refresh, kindLabel]);

  const sec = useSecureTest({
    active: phase === "run" && !!attempt, enabled: secureOn,
    integrityUrl: attempt ? `/unit-task/attempts/${attempt.attempt_id}/integrity` : "",
    initialExits: attempt?.exits || 0, maxExits: attempt?.secure?.max_exits || secureCfg?.max_exits || 2,
    onAutoSubmit: () => submit("integrity"),
  });
  secRef.current = sec;

  const screens = attempt?.screens || [];
  const screen = screens[index];
  const st = states[screen?.id] || {};
  const patch = (fn) => { dirty.current = true; setStates((prev) => ({ ...prev, [screen.id]: fn(prev[screen.id] || { doc: screen.doc, checked: [], flagged: [] }) })); };
  const setDoc = (next) => patch((s) => ({ ...s, doc: typeof next === "function" ? next(s.doc || screen.doc) : next, touched: true }));
  const toggle = (key) => (tid) => patch((s) => ({ ...s, [key]: (s[key] || []).includes(tid) ? s[key].filter((x) => x !== tid) : [...(s[key] || []), tid] }));
  const go = (i) => { stopSpeaking(); save(); setIndex(Math.max(0, Math.min(screens.length - 1, i))); };
  useEffect(() => () => stopSpeaking(), []);

  if (phase === "done" && result) return <UnitTaskResult result={result} isGuide={isGuide} />;

  if (phase === "intro") {
    if (!meta) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
    const out = !isGuide && meta.attempts_used >= meta.max_attempts && !meta.in_progress;
    const blockSpan = meta.blocks?.length > 1 ? `Blocks ${meta.blocks[0]}–${meta.blocks[meta.blocks.length - 1]}` : "the unit";
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-10" data-testid="ut-intro">
        <div className="hq-glass rounded-3xl p-8 sm:p-10 border border-[#22D3EE]/40 w-full max-w-2xl hq-fade-up">
          <button data-testid="ut-intro-back" onClick={() => navigate(`/studio/${track}`)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="w-4 h-4" /> Back to Skill Studio</button>
          <p className="text-[10px] uppercase tracking-widest text-[#22D3EE] font-mono-data">{meta.kind === "midtask" ? "Mid-unit" : "End of unit"} · hands-on task{isGuide ? " · Guide preview" : ""}</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-1">{meta.title}</h1>
          <ul className="mt-6 space-y-2 text-sm text-slate-200">
            <li className="flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /> {meta.untimed ? <span data-testid="ut-intro-untimed" className="text-[#34D399]">Untimed — your Guide has suspended the timer for you. Take the time you need.</span> : <>{meta.minutes} minutes · the timer starts when you click Start and submits automatically when it ends</>}</li>
            {meta.read_aloud && !isGuide && <li className="flex items-center gap-2" data-testid="ut-intro-readaloud"><Volume2 className="w-4 h-4 text-[#22D3EE]" /> Read-aloud is on — tap the speaker on any task, or "Read tasks" / "Read document" at the top of each screen</li>}
            <li className="flex items-center gap-2"><ListChecks className="w-4 h-4 text-[#34D399]" /> {meta.screen_count} screens · {meta.task_count} tasks covering {blockSpan}</li>
            <li className="flex items-center gap-2"><Flag className="w-4 h-4 text-[#E11D48]" /> Tick a task when you think it's done, or flag it to come back later — nothing is graded until you submit</li>
            <li className="flex items-center gap-2"><Award className="w-4 h-4 text-primary" /> {meta.points} points · pass {meta.pass}% · {meta.max_attempts} attempt{meta.max_attempts > 1 ? "s" : ""} (your Guide can reassign){meta.best_score != null ? ` · Best: ${meta.best_score}%` : ""}</li>
          </ul>
          {secureOn && <div className="mt-5 rounded-2xl border border-[#22D3EE]/30 bg-[#22D3EE]/5 p-4" data-testid="ut-secure-rules"><SecureRules maxExits={secureCfg?.max_exits || 2} compact /></div>}
          <div className="mt-8 flex items-center gap-3">
            {!meta.unlocked ? <span className="flex items-center gap-2 text-sm text-slate-400" data-testid="ut-locked"><Lock className="w-4 h-4" /> {meta.locked_reason}</span>
              : out ? <span className="text-sm text-slate-400" data-testid="ut-no-attempts">No attempts left · best {meta.best_score}%</span>
              : <Button data-testid="ut-start-btn" onClick={start} disabled={!config || (secureOn && !secureCfg)} className="bg-[#22D3EE] text-[#04121f] hover:bg-[#67E8F9] hq-glow-teal">{meta.in_progress ? `Resume ${kindLabel}` : `Start the ${kindLabel}`}{secureOn ? " in fullscreen" : ""}</Button>}
          </div>
        </div>
      </div>
    );
  }

  if (!screen) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="fixed inset-0 z-50 bg-[#060B19] flex flex-col" data-testid="ut-runner">
      <SecureOverlay away={sec.away} onResume={sec.resume} />
      <header className="border-b border-white/10 bg-[#070c1a] px-4 py-2.5 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-[#22D3EE] font-mono-data">{meta?.kind === "midtask" ? "Mid-Unit Task Test" : "Unit Final Task"} · Screen {index + 1} of {screens.length}{secureOn && <span className="ml-2 text-slate-400" data-testid="ut-secure-exits">· Secure mode{sec.exits > 0 ? ` · left ${sec.exits}×` : ""}</span>}</p>
          <h2 className="font-display text-xl truncate" data-testid="ut-screen-title">{screen.title} <span className="text-sm text-muted-foreground font-sans">· {screen.intro}</span></h2>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          {attempt.read_aloud && <ReadAloudBar screen={screen} index={index} doc={st.doc || screen.doc} />}
          <Timer deadline={attempt.deadline} onExpire={() => submit(true)} />
          <button data-testid="ut-finish-early" onClick={() => submit(false)} className="text-xs text-slate-400 hover:text-primary underline-offset-2 hover:underline">Finish early</button>
        </div>
      </header>
      <NoCopy className="px-4 py-3 border-b border-white/10 bg-[#0b1220]" testid="ut-tasks-nocopy">
        <TaskStrip screen={screen} state={st} onToggleCheck={toggle("checked")} onToggleFlag={toggle("flagged")} readAloud={attempt.read_aloud} />
      </NoCopy>
      <div className="flex-1 min-h-0 overflow-auto px-2 sm:px-4 py-3">
        {(() => {
          const kind = screen.editor || meta?.editor;
          const doc = st.doc || screen.doc;
          if (kind === "site") return <SiteEditorCore key={screen.id} doc={doc} setDoc={setDoc} config={config || {}} pageRef={pageRef} />;
          if (kind === "sheets") return <SheetEditorCore key={screen.id} doc={doc} setDoc={setDoc} config={config || {}} pageRef={pageRef} />;
          if (kind === "email") return <EmailClientCore key={screen.id} doc={doc} setDoc={setDoc} config={config || {}} proofread={null} />;
          if (kind === "design") return <DrawEditorCore key={screen.id} doc={doc} setDoc={setDoc} config={config || {}} pageRef={pageRef} />;
          if (kind === "slides") return <SlideEditorCore key={screen.id} doc={doc} setDoc={setDoc} config={config || {}} pageRef={pageRef} />;
          return <DocEditorCore key={screen.id} doc={doc} setDoc={setDoc} config={config || {}} pageRef={pageRef} issues={[]} onExportPdf={() => {}} />;
        })()}
      </div>
      <BottomBar index={index} total={screens.length} states={states} screens={screens} onGo={go} onSubmit={() => submit(false)} submitting={submitting} />
    </div>
  );
}
